import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';
import { FormBuilder } from 'shared-components/forms/form';
import DateInput from '../../elements-inputs/date-input-v2';
import { TimeSelectorInput } from '../../elements-date-time/time-selector-input';
import withCustomInputHoc from '../../elements-inputs/custom-input-hoc';
import { TimeMap } from '../../elements-date-time/data/time-data';
import { TimeUtils } from '../../elements-date-time/data/time-utils';
import { postBlockDay } from '../../app-main/actions/calendar';
import { Helpers } from 'shared-components/helpers/helpers';
import { FormattedMessage, injectIntl, defineMessages, intlShape } from 'react-intl';
import { useToast } from '@ps-design-system/components/Toast';

const TimeSelectorInForm = withCustomInputHoc(TimeSelectorInput);

const translationMessages = defineMessages({
  errorMsg: {
    id: 'hosting.block_day_form.submit_error_msg',
    defaultMessage: 'Something went wrong, please try again.'
  },
  startTime: {
    id: 'hosting.block_day_form.form_placeholder_start_time',
    defaultMessage: 'start time'
  },
  endTime: {
    id: 'hosting.block_day_form.form_placeholder_end_time',
    defaultMessage: 'end time'
  },
  startTimeErrorMsg: {
    id: 'hosting.block_day_form.form_error_msg_start_time',
    defaultMessage: 'Please enter a start time'
  },
  endTimeErrorMsg: {
    id: 'hosting.block_day_form.form_error_msg_end_time',
    defaultMessage: 'Please enter an end time'
  },
  startDate: {
    id: 'hosting.block_day_form.form_placeholder_start_date',
    defaultMessage: 'start date'
  },
  endDate: {
    id: 'hosting.block_day_form.form_placeholder_end_date',
    defaultMessage: 'end date'
  },
  formLabel: {
    id: 'hosting.block_day_form.form_label',
    defaultMessage: 'Title'
  },
  formLabelErrorMsg: {
    id: 'hosting.block_day_form.form_error_msg_title_label',
    defaultMessage: 'Please enter a title'
  },
  formPlaceholder: {
    id: 'hosting.block_day_form.form_placeholder',
    defaultMessage: 'Vacation'
  },
  formButtonText: {
    id: 'hosting.block_day_form.form_button_text',
    defaultMessage: 'Save'
  }
});

class BlockDayFormComponent extends React.Component {
  constructor (props) {
    super(props);
    this.state = {
      allDay: true,
      startTimeIndex: '',
      endTimeIndex: '',
      isOvernight: false,
      startDate: null
    };
    this.formInputs = this.formInputs.bind(this);
    this.getValueCheckbox = this.getValueCheckbox.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onSubmitCb = this.onSubmitCb.bind(this);
    this.selectStartTime = this.selectStartTime.bind(this);
    this.selectEndTime = this.selectEndTime.bind(this);
    this.getStartDate = this.getStartDate.bind(this);
    this.getAnalyticsOrigin = this.getAnalyticsOrigin.bind(this);
  }

  componentDidMount () {
    const spaceId = _.get(this.props, ['calendar', 'selectedSpace']);
    const ssoId = _.get(this.props, ['profile', 'SSOid']);
    analyticsIntegrations.blockDayFormViewed(spaceId, ssoId, this.getAnalyticsOrigin());
  }

  onSubmit (el, callback, refs, formValues) {
    const { dispatch } = this.props;
    const spaceId = _.get(this.props, ['calendar', 'selectedSpace']);
    const spaces = _.keyBy(_.get(this.props, ['calendar', 'spaces'], []), '_id');
    const latitude = (spaces[spaceId] || {}).latitude || '';
    const longitude = (spaces[spaceId] || {}).longitude || '';
    const {
      all_day: allDay,
      block_day_title: summary,
      start_date: startDate,
      end_date: endDate,
      start_time_index: startTimeIndex,
      end_time_index: endTimeIndex
    } = formValues;
    // parse input to standard schedule object
    const allDayHash = { all_day: 'true' === String(allDay) };
    const startHash = TimeUtils.scheduleHash('start', startDate, startTimeIndex);
    const endHash = TimeUtils.scheduleHash('end', endDate, endTimeIndex);
    const schedule = Object.assign({}, startHash, endHash, allDayHash);
    const payload = {
      schedule: [schedule],
      space_id: spaceId,
      transparency: 'opaque',
      visibility: 'public',
      longitude,
      latitude,
      summary: summary || undefined
    };
    dispatch(postBlockDay(payload, callback));
  }

  onSubmitCb (response = {}) {
    // response returns object { title } or { error }
    const { toggleModal, callFetchCalendar, intl } = this.props;

    if (response.error) {
      let errorMsg = intl.formatMessage(translationMessages.errorMsg);
      const isTitleError = response.missing.includes('summary');

      if (isTitleError) {
        errorMsg = intl.formatMessage(translationMessages.formLabelErrorMsg);
        return this.setState({ errorMsg });
      }
      return this.setState({ errorMsg });
    }

    const blockDayStart = _.get(response, ['events', '0'], {});
    const {
      all_day: allDay,
      space_id,
      sso_id,
      group_size: groupSize,
      start
    } = blockDayStart;
    const blockDayEnd = _.get(response, ['events', response.events.length - 1]);
    const { end } = blockDayEnd;
    const title = _.get(response, ['events', '0', 'summary'], '');
    const anaylticsPayload = {
      title,
      is_all_day: allDay,
      space_id,
      created_by: sso_id,
      count_days_blocked: groupSize,
      block_start_time: new Date(start),
      block_end_time: new Date(end),
      origin: this.getAnalyticsOrigin()
    };

    analyticsIntegrations.blockDayFormSubmitted(anaylticsPayload);
    const confirmationText = intl.formatMessage(
      { id: 'hosting.block_day_form.block_notification', defaultMessage: '“{title}” has been blocked on your calendar' },
      { title: title });

    this.props.toast({
      title: '',
      description: confirmationText,
      variant: 'success',
      duration: 5000
    });

    // Go to the start date of the blocked date / times.
    // So it updates the UI in the calendar component and page.
    callFetchCalendar(moment(start).toDate());
    return toggleModal();
  }

  getAnalyticsOrigin () {
    const { location } = this.props;
    const { hash } = location;
    const hashObj = Helpers.parseHash(hash);
    const origin = hashObj.hasOwnProperty('source') && 'decl' === hashObj.source ? 'decline confirmation' : 'calendar page';
    return origin;
  }

  getValueCheckbox (value) {
    const { allDay } = value;
    this.setState({ allDay });
  }

  onTitleChange () {
    this.setState({ errorMsg: '' });
  }

  getStartDate (date) {
    this.setState({ startDate: date });
  }

  getEndDate (date) {
    const { isOvernight, startDate } = this.state;
    if (isOvernight) {
      return moment(startDate || date).add(1, 'days').toDate();
    }
    return startDate || date;
  }

  selectStartTime (value, closeDropdownCb) {
    this.setState({ startTimeIndex: value });
    closeDropdownCb();
  }

  selectEndTime (value, closeDropdownCb) {
    const isOvernight = TimeMap[value].overnight;
    this.setState({ endTimeIndex: value, isOvernight });
    closeDropdownCb();
  }

  formInputs () {
    const selectedDate = _.get(this.props, ['calendar', 'selectedDate'], '');
    const { allDay, startTimeIndex, endTimeIndex } = this.state;
    const endDate = this.getEndDate(selectedDate);
    const { country, intl } = this.props;
    const timeInputs = [
      {
        custom: () => {
          const startTimes = _.filter(TimeMap, (value, key) => { return 35 >= key; });
          return (
            <div className="time-group col-xs-5 no-gutter-right">
              <TimeSelectorInForm
                ref="startTime"
                htmlId="start-time"
                placeholder={intl.formatMessage(translationMessages.startTime)}
                selectedTimeIndex={startTimeIndex}
                onChangeHandler={this.selectStartTime}
                errorMessage={intl.formatMessage(translationMessages.startTimeErrorMsg)}
                timeData={startTimes}
                inputFieldName="start_time_index"
              />
            </div>
          );
        }
      },
      {
        custom: () => {
          const endTimes = _.filter(TimeMap, (time) => { return startTimeIndex < time.time_index; });
          return (
            <div className="time-group col-xs-5 col-xs-offset-2 no-gutter-left">
              <TimeSelectorInForm
                ref="endTime"
                {...this.props}
                htmlId="end-time"
                placeholder={intl.formatMessage(translationMessages.endTime)}
                selectedTimeIndex={endTimeIndex}
                onChangeHandler={this.selectEndTime}
                errorMessage={intl.formatMessage(translationMessages.endTimeErrorMsg)}
                timeData={endTimes}
                inputFieldName="end_time_index"
              />
            </div>
          );
        }
      }
    ];
    const defaultInputs = [
      {
        custom: () => {
          return (
            <div className="col-xs-5 no-gutter-right">
              <DateInput
                ref="startDate"
                htmlId="startDate"
                inputFieldName="start_date"
                placeholder={intl.formatMessage(translationMessages.startDate)}
                getDate={this.getStartDate}
                defaultDate={selectedDate}
                country={country}
              />
            </div>
          );
        }
      },
      {
        custom: <div style={{ lineHeight: '42px' }} className="col-xs-2"><FormattedMessage id="hosting.block_day_form.to" defaultMessage="to"/></div>
      },
      {
        custom: () => {
          return (
            <div className="col-xs-5 no-gutter-left">
              <DateInput
                ref="endDate"
                htmlId="endDate"
                inputFieldName="end_date"
                className="col-xs-5 no-gutter-left"
                placeholder={intl.formatMessage(translationMessages.endDate)}
                defaultDate={endDate}
                arrowPlacement={{ left: '-18', arrowLeft: '200' }}
                country={country}
              />
            </div>
          );
        }
      },
      {
        inputFieldName: 'all_day',
        elementID: 'allDay',
        ref: 'blockDayAllDay',
        defaultVal: true,
        actionFn: this.getValueCheckbox,
        dataAttributes: { defaultChecked: 'checked' },
        optional: true
      },
      {
        label: intl.formatMessage(translationMessages.formLabel),
        placeholder: intl.formatMessage(translationMessages.formPlaceholder),
        onChange: () => this.onTitleChange(),
        inputFieldName: 'block_day_title',
        elementID: 'block-day-title',
        ref: 'blockDayTitle',
        labelSize: 'col-xs-12',
        maxlength: '40',
        optional: false
      }
    ];
    return allDay ? defaultInputs : [...defaultInputs.slice(0, 3), ...timeInputs, ...defaultInputs.slice(3)];
  }

  render () {
    const { errorMsg } = this.state;
    const { intl } = this.props;
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-xs-12">
            <FormBuilder
              formname="bookDayForm"
              id="book-day-form"
              formStyle="modal"
              formInputs={this.formInputs()}
              submitAction={this.onSubmit}
              submitCallback={this.onSubmitCb}
              buttonText={intl.formatMessage(translationMessages.formButtonText)}
              btnColSize="col-xs-12 margin-lg-bottom"
              buttonClass="btn btn-block btn-primary form"
              errorText={errorMsg}
            />
          </div>
        </div>
      </div>
    );
  }
}

BlockDayFormComponent.propTypes = {
  dispatch: PropTypes.func,
  intl: intlShape.isRequired,
  toast: PropTypes.func,
};

const BlockDayFormWithIntl = injectIntl(BlockDayFormComponent);

const BlockDayForm = (props) => {
  const { toast } = useToast();
  return <BlockDayFormWithIntl {...props} toast={toast} />;
};

export { BlockDayForm };
